import React, { useState, useRef, useEffect } from "react";

/** Available dropdown sizes. */
export type DropDownSize = "sm" | "md" | "lg" | "input";

/**
 * - `sm`    => h-8 (32px)
 * - `md`    => py-1.5 (~32px)
 * - `lg`    => h-12 (48px)
 * - `input` => py-2 px-3 text-sm — matches `GenericInput` default height
 *              so dropdowns and text inputs in the same row align.
 */
const sizeClasses: Record<DropDownSize, string> = {
  sm: "h-8 px-2 gap-1 text-sm",
  md: "py-1.5 px-3 gap-2 text-sm",
  lg: "h-12 px-4 gap-2 text-base",
  input: "py-2 px-3 gap-2 text-sm",
};

/** Pequeño ícono de flecha abajo. */
function ChevronDownIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={`h-4 w-4 ${className}`} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 12a.997.997 0 01-.707-.293l-4-4
           a1 1 0 111.414-1.414L10 9.586
           l3.293-3.293a1 1 0 011.414
           1.414l-4 4A.997.997 0 0110
           12z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// Type for dropdown items - can be string or object with value/label
export type DropdownItem = string | { value: string; label: string };

interface GenericSearchableDropdownProps {
  /** Array of items to display in the dropdown - can be strings or objects with value/label */
  items: DropdownItem[];
  /** Current selected value */
  value: string;
  /** Callback when selection changes */
  onSelect: (value: string) => void;
  /** Placeholder text when no value is selected */
  defaultLabel?: string;
  /** Dropdown size */
  size?: DropDownSize;
  /** CSS class for the dropdown container */
  className?: string;
  /** CSS class for width */
  widthClass?: string;
  /** CSS class for minimum width */
  minWidthClass?: string;
  /** CSS class for maximum width */
  maxWidthClass?: string;
  /** Custom input max-width (CSS value like "200px", "300px", "100%", or Tailwind class like "max-w-md") */
  inputMaxWidth?: string;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /**
   * Whether the dropdown is loading its items. Renders a spinner in place of
   * the chevron (inside the control) so the field shows it's fetching. Pair
   * with `disabled` + a "Loading…" `defaultLabel` to block interaction.
   */
  loading?: boolean;
  /** Message to show when no items are available */
  emptyItemsMessage?: string;
  /** Whether to allow custom values (not in the items list) */
  allowCustomValues?: boolean;
  /** Callback when a custom value is entered */
  onCustomValue?: (value: string) => void;
  /**
   * Render the menu with `position: fixed` (computed from the trigger's
   * viewport rect) instead of `position: absolute`. Mirrors
   * `GenericDropdown.fixedPosition` and exists for the same two reasons:
   *
   *  1. Escape ancestors with `overflow: hidden` / `overflow: auto` that
   *     would otherwise clip the menu (e.g. the Tags wizard inner scroll).
   *  2. Auto-flip upward when there is not enough room below the trigger
   *     in the viewport, so the menu stays visible without scrolling.
   *
   * Default `false` to preserve existing consumers.
   */
  fixedPosition?: boolean;
  /**
   * Max-height utility for the menu list (e.g. `"max-h-48"`). Applied to
   * the inner `<ul>` together with `overflow-y-auto`. Defaults to the
   * historical `max-h-60` when omitted.
   */
  dropdownMaxHeightClass?: string;
}

const GenericSearchableDropdown: React.FC<GenericSearchableDropdownProps> = ({
  items = [],
  value,
  onSelect,
  defaultLabel = "Select a value",
  size = "md",
  className = "",
  widthClass = "w-full",
  minWidthClass = "min-w-[140px]",
  maxWidthClass = "max-w-[200px]",
  inputMaxWidth,
  disabled = false,
  loading = false,
  emptyItemsMessage = "No options available",
  allowCustomValues = true,
  onCustomValue,
  fixedPosition = false,
  dropdownMaxHeightClass,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState(value || "");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Viewport-anchored menu position when `fixedPosition` is on. Mirrors
   * the logic in `GenericDropdown` so the two components behave
   * identically once opted in: we measure the trigger rect, decide
   * up-vs-down based on available space, and reposition on any scroll
   * (capture phase so nested scroll containers are caught too).
   */
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    openUpward: boolean;
  }>({ top: 0, left: 0, width: 0, openUpward: false });

  useEffect(() => {
    if (!isOpen || !fixedPosition) return;

    const updatePosition = () => {
      if (!dropdownRef.current) return;
      const buttonRect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 250; // estimate; actual list is capped by `dropdownMaxHeightClass`
      const headerHeight = 120; // matches GenericDropdown — avoids dock under the app header
      // Close if the trigger has scrolled out of the safe area: prevents
      // the menu from floating in space when its anchor is gone.
      if (
        buttonRect.top < headerHeight ||
        buttonRect.bottom > viewportHeight
      ) {
        setIsOpen(false);
        return;
      }
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top - headerHeight;
      const openUpward =
        spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
      setDropdownPosition({
        top: openUpward ? buttonRect.top : buttonRect.bottom,
        left: buttonRect.left,
        width: buttonRect.width,
        openUpward,
      });
    };

    updatePosition();
    const handleScroll = () => updatePosition();
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, fixedPosition]);

  // Helper function to get display label from item
  const getItemLabel = (item: DropdownItem): string => {
    return typeof item === 'string' ? item : item.label;
  };

  // Helper function to get value from item
  const getItemValue = (item: DropdownItem): string => {
    return typeof item === 'string' ? item : item.value;
  };

  // Filter items based on search term
  const filteredItems = items.filter(item =>
    getItemLabel(item).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Update input value when value prop changes - show label if available
  useEffect(() => {
    const item = items.find(item => getItemValue(item) === value);
    if (item) {
      setInputValue(getItemLabel(item));
    } else {
      setInputValue(value || "");
    }
  }, [value, items]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
        // Reset input value to the selected value if no custom value was entered
        const item = items.find(item => getItemValue(item) === value);
        const shouldReset = !allowCustomValues || !inputValue || items.some(item => getItemValue(item) === inputValue || getItemLabel(item) === inputValue);
        if (shouldReset) {
          if (item) {
            setInputValue(getItemLabel(item));
          } else {
            setInputValue(value || "");
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [value, inputValue, items, allowCustomValues]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    
    // If dropdown is not open, open it when user starts typing
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
    // Select all text when focusing
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  // Handle item selection
  const handleItemSelect = (item: DropdownItem) => {
    const itemValue = getItemValue(item);
    const itemLabel = getItemLabel(item);
    setInputValue(itemLabel);
    setSearchTerm("");
    setIsOpen(false);
    onSelect(itemValue); // Always pass the value (elementId)
  };

  // Handle input key events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen && filteredItems.length > 0) {
        // Select first filtered item
        handleItemSelect(filteredItems[0]);
      } else if (allowCustomValues && inputValue.trim()) {
        // Handle custom value
        const customValue = inputValue.trim();
        setInputValue(customValue);
        setIsOpen(false);
        setSearchTerm("");
        onSelect(customValue);
        if (onCustomValue) {
          onCustomValue(customValue);
        }
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
      const item = items.find(item => getItemValue(item) === value);
      if (item) {
        setInputValue(getItemLabel(item));
      } else {
        setInputValue(value || "");
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  // Handle dropdown toggle
  const handleDropdownToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus input when opening dropdown
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  // Get size classes for the input
  const inputSizeClass = sizeClasses[size];
  const buttonWidthClass = `${widthClass} ${minWidthClass} ${maxWidthClass}`;

  return (
    <div
      ref={dropdownRef}
      className={`relative ${className}`}
    >
      {/* Input field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={value ? "" : defaultLabel}
          disabled={disabled}
          className={`
            ${buttonWidthClass} ${inputSizeClass}
            text-semantic-text-secondary bg-semantic-bg-primary-alt
            border border-semantic-border-secondary rounded-sm
            disabled:opacity-50 disabled:cursor-not-allowed
            disabled:hover:bg-semantic-bg-primary-alt
            hover:bg-semantic-state-active transition-colors
            focus:outline-none
            pr-8
            ${inputMaxWidth && inputMaxWidth.startsWith("max-w-") ? inputMaxWidth : ""}
          `}
          style={{
            ...(inputMaxWidth && (inputMaxWidth.includes("px") || inputMaxWidth.includes("%") || inputMaxWidth.includes("rem") || inputMaxWidth.includes("em") || inputMaxWidth.includes("vw"))
              ? { maxWidth: inputMaxWidth }
              : {}),
          }}
        />
        
        {/* Dropdown arrow */}
        <button
          type="button"
          onClick={handleDropdownToggle}
          disabled={disabled}
          className={`
            absolute right-2 top-1/2 transform -translate-y-1/2
            p-1 rounded
            text-semantic-text-secondary
            hover:text-semantic-text-primary
            focus:outline-none
            disabled:opacity-50 disabled:cursor-not-allowed
            disabled:hover:text-semantic-text-secondary
            transition-transform duration-200
            ${isOpen && !loading ? 'rotate-180' : ''}
          `}
        >
          {loading ? (
            <span
              className="ml-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-semantic-border-secondary border-t-semantic-bg-brand-bold-secondary"
              role="status"
              aria-label="Loading"
            />
          ) : (
            <ChevronDownIcon className="ml-2" />
          )}
        </button>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={`
            ${!fixedPosition ? "absolute mt-1" : "fixed"}
            ${widthClass} ${minWidthClass} ${maxWidthClass}
            bg-semantic-bg-primary-alt
            border border-semantic-border-secondary
            rounded-sm
            shadow-md
            ${fixedPosition ? "z-[999]" : "z-50"}
          `}
          style={
            fixedPosition
              ? {
                  top: dropdownPosition.openUpward
                    ? "auto"
                    : `${dropdownPosition.top}px`,
                  bottom: dropdownPosition.openUpward
                    ? `${window.innerHeight - dropdownPosition.top}px`
                    : "auto",
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                }
              : undefined
          }
        >
          <ul
            className={`py-1 ${
              dropdownMaxHeightClass ?? "max-h-60"
            } overflow-y-auto`}
          >
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <li key={index}>
                  <button
                    type="button"
                    onClick={() => handleItemSelect(item)}
                    className="
                      w-full text-left
                      px-3 py-2
                      text-sm
                      text-semantic-text-secondary
                      hover:bg-semantic-state-active
                      hover:text-semantic-text-brand
                      transition-colors
                    "
                  >
                    {getItemLabel(item)}
                  </button>
                </li>
              ))
            ) : (
              <li>
                <div className="flex items-center justify-center px-3 py-2 text-sm text-semantic-text-secondary cursor-default">
                  <span>{searchTerm ? "No matching items" : emptyItemsMessage}</span>
                </div>
              </li>
            )}
            
            {/* Show option to add custom value if no exact match and custom values are allowed */}
            {allowCustomValues && searchTerm && !items.some(item => getItemValue(item) === searchTerm || getItemLabel(item) === searchTerm) && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    const customValue = searchTerm.trim();
                    setInputValue(customValue);
                    setIsOpen(false);
                    setSearchTerm("");
                    onSelect(customValue);
                    if (onCustomValue) {
                      onCustomValue(customValue);
                    }
                  }}
                  className="
                    w-full text-left
                    px-3 py-2
                    text-sm
                    text-semantic-text-brand
                    hover:bg-semantic-state-active
                    hover:text-semantic-text-brand
                    transition-colors
                    border-t border-semantic-border-secondary
                  "
                >
                  Add &quot;{searchTerm}&quot;
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GenericSearchableDropdown;
